const {
  locales,
  defaultLocale,
  hreflangOf,
  toContentLocale,
} = require("./locales");

/**
 * WHO-6534. The path of the value check ("Wat is jouw item waard?") per locale. `pages/value-check.tsx`
 * is the one route; every other locale serves its own word through a rewrite and 301s the other
 * words onto it (Thomas, 27 Oct 2026: localised paths).
 *
 * A dependency-free CJS leaf, like `pro-buyer-paths.js`: `rewrites.js` and `redirects.js`
 * `require()` it, and the feature's static file imports it for the in-app links. The same words
 * are in `lib/multilingual/locales/*.json` (key `value-check`) so Canonical, HrefLang and the
 * language switcher localize the path; a test keeps them in step.
 */
const VALUE_CHECK_WORDS = {
  en: "value-check",
  nl: "waardecheck",
  de: "wertcheck",
  fr: "estimation",
  it: "valutazione",
  es: "tasacion",
};

/** The internal route every locale renders. */
const VALUE_CHECK_PAGE = `/${VALUE_CHECK_WORDS[defaultLocale]}`;

/**
 * The page's path in a locale, without the locale prefix. `en-us` uses the English word (WHO-5766);
 * unknown locales get the default.
 */
const getValueCheckPath = locale =>
  `/${
    VALUE_CHECK_WORDS[toContentLocale(locale)] ??
    VALUE_CHECK_WORDS[defaultLocale]
  }`;

/** The public path including the locale prefix (none for the default locale). */
const getValueCheckPublicPath = locale =>
  locale && locale !== defaultLocale
    ? `/${locale}${getValueCheckPath(locale)}`
    : getValueCheckPath(defaultLocale);

const prefixedLocales = locales.filter(locale => locale !== defaultLocale);

/** `/nl/waardecheck` serves `/nl/value-check`. */
const valueCheckRewrites = () =>
  prefixedLocales
    // `/en-us` uses the English word, which is the route itself: nothing to rewrite.
    .filter(locale => getValueCheckPath(locale) !== VALUE_CHECK_PAGE)
    .map(locale => ({
      source: `/${locale}${getValueCheckPath(locale)}`,
      destination: `/${locale}${VALUE_CHECK_PAGE}`,
      locale: false,
    }));

/**
 * `/nl/value-check` and `/nl/wertcheck` 301 to `/nl/waardecheck`, so no locale answers 200 on
 * two URLs. Only mismatched pairs are generated, so the pair each rewrite serves is never
 * redirected. The default locale gets no rule: `/en/`-prefixed sources are forbidden (WHO-5477)
 * and English is unprefixed `/value-check`, which is the route itself.
 */
const valueCheckRedirects = () =>
  prefixedLocales.flatMap(locale => {
    const own = VALUE_CHECK_WORDS[toContentLocale(locale)];

    return [...new Set(Object.values(VALUE_CHECK_WORDS))]
      .filter(word => word !== own)
      .map(word => ({
        source: `/${locale}/${word}`,
        destination: `/${locale}/${own}`,
        permanent: true,
        locale: false,
      }));
  });

/**
 * The page in the sitemap since the gate went to everyone (Thomas, 5 Nov 2026): every locale's URL
 * once, each with the full hreflang cluster. Server-rendered, so the prerender pass never lists it.
 */
const valueCheckSitemapEntries = canonicalHost => {
  const alternates = [
    {
      hreflang: "x-default",
      href: `${canonicalHost}${getValueCheckPublicPath(defaultLocale)}`,
    },
    ...locales.map(locale => ({
      hreflang: hreflangOf(locale),
      href: `${canonicalHost}${getValueCheckPublicPath(locale)}`,
    })),
  ];

  return locales.map(locale => ({
    loc: getValueCheckPublicPath(locale),
    alternates,
  }));
};

module.exports = {
  valueCheckSitemapEntries,
  VALUE_CHECK_WORDS,
  VALUE_CHECK_PAGE,
  getValueCheckPath,
  getValueCheckPublicPath,
  valueCheckRewrites,
  valueCheckRedirects,
};
